// React & Hooks
import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";


// Helpers
import { transformRoute, useTranslate } from "helpers";

// Pagination & Search
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// API & Types
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { deleteFleetTireSetAPI, getFleetTiresSetAPI } from "services/api/fleet/tires/api";
import { VehicleTireSetCardProps } from "./types";


// UI Components
import { Button } from "ui/components/Button/Button";
import { ButtonLink } from "ui/components/Button/ButtonLink";
import { NoResults } from "ui/components/NoResults";

// Navigation & Titles
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import {

  FLEET_VEHICLE_TIRE_SET_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_TIRE_SET_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Local Components
import { FleetTireSetLocationEntityType, FleetTireSetRow } from "services/api/fleet/tires/types";
import { Dialog } from "ui/components/Dialog";
import TireSetDemountEditCard from "ui/modules/fleet/tires/pages/TireSetDemountEditCard/TireSetDemountEditCard";
import TireSetEditCard from "ui/modules/fleet/tires/pages/TireSetEditCard/TireSetEditCard";
import VehicleTireSetSearchResults from "./components/VehicleTireSearchResults/VehicleTireSetSearchResults";


const VehicleTireSetCard: React.FC<VehicleTireSetCardProps> = (props) => {
  const { fleetVehicleDetail } = props;

  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetTireSetRow>
  >(initialDataWithPagination);

  const [tireSetId, setTireSetId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showDemount, setShowDemount] = useState(false);
  const [transferTireSetId, setTransferTireSetId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  const fetchSearchResult = async (
    pagination: Pagination,
    vehicleId: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetTiresSetAPI(pagination, null, vehicleId);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setTireSetId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setTireSetId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(newPagination, fleetVehicleDetail.id);
  };

  const handleEditSuccess = useCallback(() => {
    setShowAddNewModal(false);

    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fetchSearchResult]);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setTireSetId(id);
    setOpenDialog(true);

  }

  const handleLocationClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, entityId: string, entityName: string) => {
    event.stopPropagation();
    event.preventDefault();

    let route: string;

    switch (entityName) {
      case FleetTireSetLocationEntityType.Vehicle:
        route = transformRoute(
          FleetVehicleRoutes.FleetVehicleDetailPageRouter,
          { id: entityId }
        );
        break;
      case FleetTireSetLocationEntityType.Warehouse:
        route = transformRoute(
          "/fleet/warehouse/:id/detail/",
          { id: entityId }
        );
        break;
      default:
        return;
    }

    navigate(route);
  }, [navigate]);

  const handleEditClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTireSetId(id);
    setShowAddNewModal(true);
  }, []);

  const handleTransferClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTransferTireSetId(id);
    setShowDemount(true);
  }, []);

  const handleTransferSuccess = useCallback(() => {
    if (fleetVehicleDetail.id) fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
    setTransferTireSetId("");
    setShowDemount(false);
  }, [fleetVehicleDetail.id, searchResults.pagination]);

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!tireSetId) return;

    try {
      const deleted = await deleteFleetTireSetAPI(
        tireSetId,
        t
      );

      if (deleted && fleetVehicleDetail.id) {
        await fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setTireSetId("");
    } catch (err) {
      console.error("Greška prilikom brisanja vozača:", err);
    }
  }, [tireSetId, t, fleetVehicleDetail.id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setTireSetId("");
    setOpenDialog(false);
  }, []);


  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="space-y-3 ">
        <div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase ">

            </div>
            <div className="flex items-center gap-2 px-3 my-3 ">
              <div className="">
                <ButtonLink
                  size="small"
                  variant="warning"
                  children={t("admin.fleet.vehicle.tire.detail")}
                  to={transformRoute(
                    FleetVehicleRoutes.FleetVehicleTireSetSearchPageRouter,
                    {
                      id: fleetVehicleDetail.id,
                    }
                  )}
                  startIcon="/media/icons/search.svg"

                />
              </div>
              <div>
                <Button
                  size="small"
                  variant="secondary"
                  children={t("admin.fleet.vehicle.tire.set.add")}
                  onClick={handleAddNew}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
          </div>
          <div className="m-[-1px]">
            {!isLoading && searchResults.pagination.totalPages === 0 ? (
              <NoResults
                message={t("admin.fleet.vehicle.tire.search.noresults")}
              />
            ) : (
              <VehicleTireSetSearchResults
                searchResults={searchResults}
                selectedRows={selectedRows}
                isLoading={isLoading}
                onRowClick={handleRowClick}
                onPaginationChange={handlePaginationChange}
                onSelectedChange={handleSelectedChange}
                onDeleteClick={handleDeleteClick}
                onLocationClick={handleLocationClick}
                onEditClick={handleEditClick}
                onDemountClick={handleTransferClick}
              />
            )}
          </div>
        </div>
      </div>

      <TireSetEditCard
        show={showAddNewModal}
        vehicleId={fleetVehicleDetail.id}
        tireSetId={tireSetId}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
        modalIcon="/media/images/add-icon.svg"
        modalTitle={
          tireSetId
            ? FLEET_VEHICLE_TIRE_SET_EDIT_PAGE_TITLE.text
            : FLEET_VEHICLE_TIRE_SET_CREATE_PAGE_TITLE.text
        }
        modalPosition="side"
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
      <TireSetDemountEditCard
        show={showDemount}
        tireSetId={transferTireSetId}
        onSuccess={handleTransferSuccess}
        onClose={() => { setShowDemount(false); setTransferTireSetId(""); }}
      />
    </>
  );
};

export default memo(VehicleTireSetCard);
